import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PersonAdd, ArrowForward } from '@mui/icons-material';
import { GoogleOAuthProvider } from '@react-oauth/google';

import RegisterForm from '../forms/RegisterForm';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

function Register() {
  const navigate = useNavigate();
  const config = useConfig();

  // Obsługa pomyślnego logowania przez OAuth
  const handleOAuthSuccess = () => {
    setTimeout(() => {
      navigate('/');
    }, 500);
  };

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <>
      <motion.div
        className="min-h-[80vh] w-full flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="w-full max-w-2xl" variants={itemVariants}>
          <div className="text-center mb-8">
            <motion.div
              className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30,
                delay: 0.3,
              }}
            >
              <PersonAdd className="h-8 w-8 text-primary" />
            </motion.div>

            <motion.h1
              className="text-3xl font-bold text-gray-900"
              variants={itemVariants}
            >
              Utwórz nowe konto
            </motion.h1>

            <motion.p className="mt-2 text-gray-600" variants={itemVariants}>
              Dołącz do nas, aby korzystać z pełni możliwości sklepu{' '}
              {config.company.name}
            </motion.p>
          </div>

          <motion.div
            className="bg-white py-8 px-6 shadow-xl sm:rounded-lg sm:px-10 border border-gray-100"
            variants={itemVariants}
            whileHover={{
              boxShadow:
                '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
            }}
            transition={{ duration: 0.3 }}
          >
            <RegisterForm />

            <motion.div
              className="mt-8 pt-6 border-t border-gray-200"
              variants={itemVariants}
            >
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Lub załóż konto z
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <GoogleOAuthProvider
                  clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}
                >
                  <GoogleLoginButton onSuccess={handleOAuthSuccess} />
                </GoogleOAuthProvider>
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Masz już konto?
                  <motion.div
                    className="inline-block"
                    whileHover={{ x: 5 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    <Link
                      to="/logowanie"
                      className="ml-2 font-medium text-primary hover:text-primary-dark items-center justify-center gap-1 inline-flex"
                    >
                      Zaloguj się
                      <ArrowForward fontSize="small" />
                    </Link>
                  </motion.div>
                </p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="text-center mt-6 text-sm text-gray-500"
          >
            Rejestrując się akceptujesz nasz{' '}
            <a href="/regulamin" className="text-primary hover:underline">
              Regulamin
            </a>{' '}
            oraz{' '}
            <a href="/prywatnosc" className="text-primary hover:underline">
              Politykę Prywatności
            </a>
          </motion.div>
        </motion.div>
      </motion.div>
      <Seo
        title={`Rejestracja`}
        description={config.company.about.shortInfo || ''}
        image={config.company.logo}
        noindex={true}
      ></Seo>
    </>
  );
}

export default Register;
